import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, ReplaySubject, Subscription, filter, map, switchMap, tap } from 'rxjs';

import { OauthService, SelectableAccount, UserContextService } from '@hdms/shared/data-access';
import { UserAccount, UserAccountData } from '@hdms/shared/page-frame/types';
import {
  AnalyticsService,
  EnvironmentService,
  LogoutService,
  PlatformService,
  RedirectService,
  TabSyncService
} from '@hdms/shared/util-core';

function transformAccount({ account: { address, originId }, contact }: SelectableAccount): UserAccount {
  return {
    originId,
    companyName: address?.company,
    formatted: address?.formatted,
    contact
  };
}

function transformUserData({
  contact,
  account,
  isLoggedIn,
  isRegistered,
  isAccountSync,
  isEmployee,
  userAccounts,
  error,
  isEVoucherBackofficeUser
}: UserContextService): UserAccountData {
  isLoggedIn &&= !error;

  const userState: UserAccountData = {
    isLoggedIn,
    isRegistered,
    isAccountSync,
    isEmployee,
    accountMenuEnabled: isRegistered && !isAccountSync && !isEVoucherBackofficeUser
  };

  return isLoggedIn
    ? {
        accounts: userAccounts.map(transformAccount),
        selectedAccount: transformAccount({ account, contact }),
        ...userState
      }
    : userState;
}

type TabSyncData = Pick<UserAccount, 'originId'>;

@Injectable({
  providedIn: 'root'
})
export class HeaderUserService implements OnDestroy {
  private readonly tabAccountChanged = new ReplaySubject<boolean>(1);
  private readonly userDataSubject = new BehaviorSubject<UserAccountData>({ isLoading: true });

  private userDataSubscription: Subscription;

  constructor(
    private readonly envService: EnvironmentService,
    private readonly userContext: UserContextService,
    private readonly oauthService: OauthService,
    private readonly redirectService: RedirectService,
    private readonly tabSyncService: TabSyncService,
    private readonly logoutService: LogoutService,
    private readonly platform: PlatformService,
    private readonly analyticsService: AnalyticsService
  ) {}

  // will be executed in SSR env
  ngOnDestroy(): void {
    this.userDataSubscription?.unsubscribe();
  }

  public get tabAccountChanged$(): Observable<boolean> {
    return this.tabAccountChanged.asObservable();
  }

  public getUserData$(): Observable<UserAccountData> {
    this.userDataSubscription ||= this.loadUserData();
    return this.userDataSubject.asObservable();
  }

  public setSelectedAccount(originId: string): void {
    this.setLoadingState();
    this.logoutService.dispatchAccountSwitch();
    this.analyticsService.sendEvent({
      action: 'Account',
      category: 'UserAccountFlyout',
      label: 'SwitchAccount'
    });

    if (!this.envService.hostContext) {
      this.oauthService.getAccountSwitchUrl$(originId).subscribe(url => this.redirectService.redirectTo(url));
    }
  }

  public setLoadingState(): void {
    this.userDataSubject.next({ ...this.userDataSubject.value, isLoading: true });
  }

  /**
   * Load initial user-context.
   * As loading also controls login-button preloader,
   * in order to keep preloader active, don't subscribe
   * - if on server side and sso cookie is present (preventUserDataCaching)
   * - if on server side and in AEM/Hybris context (hostContext)
   * and pass subscription filter only
   * - if user is not being redirected to cloud login (requiresLogin)
   * - if there's a userContext.error
   *
   * @private
   */
  private loadUserData(): Subscription {
    return !this.userContext.preventUserDataCaching && !(this.platform.isServer && this.envService.hostContext)
      ? this.userContext
          .update$()
          .pipe(
            filter(_ => !this.userContext.requiresLogin || !!this.userContext.error),
            map(_ => transformUserData(this.userContext)),
            tap(this.onUserDataReceived.bind(this)),
            switchMap(({ selectedAccount }) => this.checkTabAccountChange$(selectedAccount))
          )
          .subscribe()
      : null;
  }

  private onUserDataReceived(data: UserAccountData): void {
    this.userDataSubject.next(data);
    this.tabSyncService.postMessage<TabSyncData>({ originId: data?.selectedAccount?.originId });
  }

  private checkTabAccountChange$(account: UserAccount): Observable<unknown> {
    return this.tabSyncService.onMessage$<TabSyncData>('originId').pipe(
      filter(({ originId }) => originId !== account?.originId),
      tap(_ => this.tabAccountChanged.next(true))
    );
  }
}
